import type { Callout } from './callouts.js';

/**
 * The dealer's queue rules, with no player attached: which call speaks next,
 * which is dropped, and which cuts the one talking. The browser engine
 * (`apps/web/src/lib/voice.ts`) and the TV's expo-audio engine both ask these
 * questions, and a dealer that behaves differently on the big screen than in
 * the browser would be a bug nobody could reproduce — so the answers live
 * here, as data.
 *
 * - **One at a time.** A call speaks until its clips are done.
 * - **`high` cuts.** A high call clears everything below it in the queue and
 *   interrupts a lower call mid-word.
 * - **`low` yields.** A low call is dropped outright if anything is already
 *   waiting: by the time it would be heard, it is chatter.
 * - **Stale is silent.** A call that has waited longer than `STALE_MS` for
 *   its turn is dropped. Late is worse than silent — "Seven out" over the
 *   next hand's deal is noise.
 */

/** A call that has waited longer than this is not worth making. */
export const STALE_MS = 2_000;

/** A call waiting its turn, and when it was earned. */
export interface QueuedCall {
  call: Callout;
  at: number;
}

/** What `admit` decided: the new queue, and whether to cut what is speaking. */
export interface Admission {
  queue: QueuedCall[];
  /** True when a high call has to interrupt the lower call speaking now. */
  cut: boolean;
  /** False when the call was dropped outright (a `low` call with a backlog). */
  queued: boolean;
}

/**
 * Take one earned call. `speaking` is the call the engine has on air, if any.
 */
export function admit(
  queue: readonly QueuedCall[],
  call: Callout,
  speaking: Callout | null,
  now: number,
): Admission {
  if (call.priority === 'high') {
    const kept = queue.filter((q) => q.call.priority === 'high');
    return {
      queue: [...kept, { call, at: now }],
      cut: speaking !== null && speaking.priority !== 'high',
      queued: true,
    };
  }
  if (call.priority === 'low' && queue.length > 0) {
    return { queue: [...queue], cut: false, queued: false };
  }
  return { queue: [...queue, { call, at: now }], cut: false, queued: true };
}

/** What `pump` found: the call to speak now (null = nothing left), and the rest. */
export interface NextCall {
  call: Callout | null;
  queue: QueuedCall[];
}

/** The next call worth making, dropping whatever went stale behind it. */
export function nextDue(queue: readonly QueuedCall[], now: number): NextCall {
  const rest = [...queue];
  let next: QueuedCall | undefined;
  while ((next = rest.shift())) {
    if (now - next.at <= STALE_MS) return { call: next.call, queue: rest };
  }
  return { call: null, queue: rest };
}

/**
 * How long a caption with nothing to say it out loud stays up: about as long
 * as reading the words takes.
 */
export const captionMs = (text: string): number => Math.min(4_000, 600 + text.length * 55);
